import { useState, useMemo } from "react";
import PortalLayout from "@/components/PortalLayout";
import { supabase } from "@/integrations/supabase/client";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Ship, MapPin, Users, Gauge, Anchor, ChevronRight, LayoutGrid, List,
  Shield, Calendar, FileText, Printer, X, BookOpen, ArrowLeft,
  CheckCircle2, AlertTriangle, Clock, Wrench
} from "lucide-react";
import { usePortalCompany } from "@/hooks/usePortalCompany";
import {
  MOCK_VESSELS, MOCK_CERTIFICATES, MOCK_AUDITS, MOCK_MAINTENANCE,
  resolveClientKey, getClientVessels, getClientCertificates,
  getClientMaintenance, getClientAudits, VESSEL_CATALOGUE,
} from "@/data/portalMockData";
import vesselRender1 from "@/assets/vessel-render-1.jpg";
import vesselRender2 from "@/assets/vessel-render-2.jpg";
import vesselRender3 from "@/assets/vessel-render-3.jpg";
import aircatSonangol from "@/assets/aircat-sonangol.jpg";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/hooks/use-toast";

const vesselImages = [vesselRender1, vesselRender2, vesselRender3];

const statusConfig: Record<string, { label: string; color: string }> = {
  on_charter: { label: "ON-HIRE", color: "bg-emerald-500 text-white" },
  available: { label: "ON-HIRE", color: "bg-emerald-500 text-white" },
  standby: { label: "STANDBY", color: "bg-amber-500 text-[hsl(215,25%,17%)]" },
  in_transit: { label: "IN TRANSIT", color: "bg-blue-500 text-white" },
  alongside: { label: "ON-HIRE", color: "bg-emerald-500 text-white" },
  maintenance: { label: "MAINTENANCE", color: "bg-orange-500 text-white" },
  off_hire: { label: "OFF-HIRE", color: "bg-red-500 text-white" },
};

const VesselStatusModal = ({ vessel, onClose }: { vessel: any; onClose: () => void }) => {
  const vesselCerts = MOCK_CERTIFICATES.filter(c => c.vessel === vessel.name);
  const vesselMaint = MOCK_MAINTENANCE.filter(m => m.vessel === vessel.name);
  const vesselAudits = MOCK_AUDITS.filter(a => a.vessel === vessel.name);

  const handlePrint = () => {
    const printContent = document.getElementById("vessel-status-print");
    if (!printContent) return;
    const w = window.open("", "_blank");
    if (!w) return;
    w.document.write(`<html><head><title>Vessel Status — ${vessel.name}</title>
      <style>body{font-family:Arial,sans-serif;padding:40px;color:#222}
      h1{font-size:22px;margin-bottom:4px}h2{font-size:16px;margin-top:24px;border-bottom:2px solid #0066CC;padding-bottom:4px}
      table{width:100%;border-collapse:collapse;margin-top:8px}td,th{border:1px solid #ddd;padding:6px 10px;text-align:left;font-size:12px}
      th{background:#f5f7fa;font-weight:600}.badge{display:inline-block;padding:2px 8px;border-radius:4px;font-size:10px;font-weight:700}
      .valid{background:#d4edda;color:#155724}.expiring{background:#fff3cd;color:#856404}</style></head>
      <body>${printContent.innerHTML}<p style="margin-top:30px;font-size:10px;color:#999">Generated ${new Date().toLocaleDateString()} — Velocity Offshore Holdings LLC</p></body></html>`);
    w.document.close();
    w.print();
  };

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Ship className="w-5 h-5 text-[hsl(211,100%,40%)]" />
            {vessel.name} — Vessel Status
          </DialogTitle>
        </DialogHeader>

        <div id="vessel-status-print">
          {/* General Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {[
              { label: "Vessel Type", value: vessel.type },
              { label: "Flag State", value: vessel.flag },
              { label: "Class Society", value: vessel.classSociety },
              { label: "IMO Number", value: vessel.imoNumber },
              { label: "Build Year", value: vessel.buildYear },
              { label: "Builder", value: vessel.builder },
              { label: "LOA / Beam", value: `${vessel.loa} / ${vessel.beam}` },
              { label: "Home Port", value: vessel.homePort },
            ].map(item => (
              <div key={item.label} className="p-3 rounded-lg bg-[hsl(220,20%,97%)]">
                <p className="text-[9px] text-[hsl(215,12%,50%)] uppercase tracking-wider">{item.label}</p>
                <p className="text-sm font-medium text-[hsl(215,25%,17%)] mt-0.5">{item.value}</p>
              </div>
            ))}
          </div>

          {/* Drydock & Survey Status */}
          <h2 className="text-sm font-bold text-[hsl(215,25%,17%)] mb-3 flex items-center gap-2">
            <Wrench className="w-4 h-4 text-[hsl(211,100%,40%)]" /> Drydock & Survey Status
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
            {[
              { label: "Last Drydock", value: vessel.buildYear >= 2026 ? "New Build — N/A" : "Jan 2025", icon: Wrench },
              { label: "Next Drydock Due", value: vessel.buildYear >= 2026 ? "Jan 2029" : "Jan 2027", icon: Calendar },
              { label: "Last Class Survey", value: "Mar 2025", icon: Shield },
              { label: "Last Statutory Survey", value: "Feb 2025", icon: FileText },
              { label: "Last Internal Audit", value: vesselAudits.find(a => a.type === "Internal")?.date || "Mar 2025", icon: CheckCircle2 },
              { label: "Last External Audit", value: vesselAudits.find(a => a.type === "Client" || a.type === "PSC" || a.type === "Flag State")?.date || "Feb 2025", icon: Shield },
            ].map(item => {
              const Icon = item.icon;
              return (
                <div key={item.label} className="p-3 rounded-lg border border-[hsl(220,15%,90%)] flex items-start gap-2">
                  <Icon className="w-4 h-4 text-[hsl(211,100%,40%)] mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-[9px] text-[hsl(215,12%,50%)] uppercase tracking-wider">{item.label}</p>
                    <p className="text-sm font-medium text-[hsl(215,25%,17%)]">{item.value}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Class & Statutory Certificates */}
          <h2 className="text-sm font-bold text-[hsl(215,25%,17%)] mb-3 flex items-center gap-2">
            <Shield className="w-4 h-4 text-[hsl(211,100%,40%)]" /> Class & Statutory Certificates
          </h2>
          {vesselCerts.length === 0 ? (
            <p className="text-sm text-[hsl(215,12%,50%)] mb-6">No certificates on record.</p>
          ) : (
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-[hsl(220,20%,97%)] border-b border-[hsl(220,15%,90%)]">
                    {["Certificate", "Cert No.", "Issuing Authority", "Issue Date", "Expiry Date", "Status"].map(h => (
                      <th key={h} className="px-3 py-2 text-left font-medium text-[hsl(215,12%,50%)]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {vesselCerts.map(cert => (
                    <tr key={cert.id} className="border-b border-[hsl(220,15%,92%)]">
                      <td className="px-3 py-2 font-medium text-[hsl(215,25%,17%)]">{cert.name}</td>
                      <td className="px-3 py-2 text-[hsl(215,12%,50%)]">{cert.certNo}</td>
                      <td className="px-3 py-2 text-[hsl(215,12%,50%)]">{cert.issuingAuthority}</td>
                      <td className="px-3 py-2">{cert.issueDate}</td>
                      <td className="px-3 py-2">{cert.expiryDate}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${
                          cert.status === "valid" ? "bg-green-100 text-green-700" :
                          cert.status === "expiring" ? "bg-amber-100 text-amber-700" :
                          "bg-red-100 text-red-700"
                        }`}>{cert.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Audit History */}
          <h2 className="text-sm font-bold text-[hsl(215,25%,17%)] mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4 text-[hsl(211,100%,40%)]" /> Audit History
          </h2>
          {vesselAudits.length === 0 ? (
            <p className="text-sm text-[hsl(215,12%,50%)] mb-6">No audit records.</p>
          ) : (
            <div className="overflow-x-auto mb-6">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-[hsl(220,20%,97%)] border-b border-[hsl(220,15%,90%)]">
                    {["Date", "Type", "Findings", "Status"].map(h => (
                      <th key={h} className="px-3 py-2 text-left font-medium text-[hsl(215,12%,50%)]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {vesselAudits.map(audit => (
                    <tr key={audit.id} className="border-b border-[hsl(220,15%,92%)]">
                      <td className="px-3 py-2">{audit.date}</td>
                      <td className="px-3 py-2 font-medium">{audit.type}</td>
                      <td className="px-3 py-2">{audit.findings}</td>
                      <td className="px-3 py-2">
                        <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-green-100 text-green-700">{audit.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Upcoming Maintenance */}
          <h2 className="text-sm font-bold text-[hsl(215,25%,17%)] mb-3 flex items-center gap-2">
            <Wrench className="w-4 h-4 text-[hsl(211,100%,40%)]" /> Scheduled Maintenance
          </h2>
          {vesselMaint.filter(m => m.status !== "complete").length === 0 ? (
            <p className="text-sm text-[hsl(215,12%,50%)]">No upcoming maintenance items.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-[hsl(220,20%,97%)] border-b border-[hsl(220,15%,90%)]">
                    {["Job ID", "System", "Description", "Due Date", "Assigned To", "Status"].map(h => (
                      <th key={h} className="px-3 py-2 text-left font-medium text-[hsl(215,12%,50%)]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {vesselMaint.filter(m => m.status !== "complete").map(job => (
                    <tr key={job.id} className="border-b border-[hsl(220,15%,92%)]">
                      <td className="px-3 py-2 font-medium">{job.jobId}</td>
                      <td className="px-3 py-2">{job.system}</td>
                      <td className="px-3 py-2">{job.description}</td>
                      <td className="px-3 py-2">{job.dueDate}</td>
                      <td className="px-3 py-2">{job.assignedTo}</td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${
                          job.status === "overdue" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"
                        }`}>{job.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-4 pt-4 border-t border-[hsl(220,15%,90%)]">
          <Button variant="outline" size="sm" onClick={handlePrint}>
            <Printer className="w-3.5 h-3.5 mr-1" /> Print Vessel Status
          </Button>
          <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const CatalogueVesselCard = ({ vessel }: { vessel: typeof VESSEL_CATALOGUE[0] }) => {
  const [enquirySent, setEnquirySent] = useState(false);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [showLightbox, setShowLightbox] = useState(false);

  const handleSubmitEnquiry = async () => {
    if (!contactName.trim() || !contactEmail.trim()) {
      toast({ title: "Missing Information", description: "Please provide your name and email address.", variant: "destructive" });
      return;
    }
    // Create announcement for Bo, Jerome and Kyle
    try {
      await supabase.from("announcements").insert({
        title: `Vessel Enquiry: ${vessel.name}`,
        content: `A client has requested more information about the ${vessel.name} (${vessel.type}).\n\nContact: ${contactName}\nEmail: ${contactEmail}${contactPhone ? `\nPhone: ${contactPhone}` : ""}\n\nPlease follow up at your earliest convenience.`,
        priority: "high",
        is_pinned: true,
      });
    } catch (e) { /* silent */ }
    setEnquirySent(true);
    setShowContactForm(false);
    toast({ title: "✅ Enquiry Submitted", description: `Our commercial team will reach out to you shortly regarding the ${vessel.name}.` });
  };

  return (
    <div className="portal-card overflow-hidden">
      {/* Full vessel image — edge-to-edge, clickable for lightbox */}
      <div className="w-full overflow-hidden cursor-pointer group relative" onClick={() => setShowLightbox(true)}>
        <img
          src={vessel.image}
          alt={vessel.name}
          className="w-full h-[280px] object-cover transition-transform duration-500 group-hover:scale-105"
          loading="eager"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center">
          <span className="opacity-0 group-hover:opacity-100 transition-opacity text-white text-xs font-semibold bg-black/50 px-3 py-1.5 rounded-full backdrop-blur-sm">Click to enlarge</span>
        </div>
      </div>

      {/* Lightbox */}
      {showLightbox && (
        <div className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4 cursor-pointer" onClick={() => setShowLightbox(false)}>
          <button className="absolute top-4 right-4 text-white/80 hover:text-white z-10" onClick={() => setShowLightbox(false)}>
            <X className="w-8 h-8" />
          </button>
          <img
            src={vessel.image}
            alt={vessel.name}
            className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={e => e.stopPropagation()}
          />
          <div className="absolute bottom-6 left-0 right-0 text-center">
            <p className="text-white font-bold text-lg">{vessel.name}</p>
            <p className="text-white/60 text-xs uppercase tracking-[2px]">{vessel.type}</p>
          </div>
        </div>
      )}
      <div className="p-5 space-y-4">
        <div>
          <h3 className="font-bold text-xl text-[hsl(215,25%,17%)]">{vessel.name}</h3>
          <p className="text-[11px] font-semibold uppercase tracking-[2px] text-[hsl(185,72%,42%)] mt-0.5">{vessel.type}</p>
        </div>
        <p className="text-xs text-[hsl(215,12%,50%)] leading-relaxed">{vessel.description}</p>
        <div className="grid grid-cols-2 gap-2">
          {vessel.specs.map(spec => (
            <div key={spec.label} className="p-2 rounded bg-[hsl(220,20%,97%)]">
              <p className="text-[9px] text-[hsl(215,12%,50%)] uppercase tracking-wider">{spec.label}</p>
              <p className="text-xs font-medium text-[hsl(215,25%,17%)]">{spec.value}</p>
            </div>
          ))}
        </div>

        {/* Contact form */}
        {showContactForm && !enquirySent && (
          <div className="space-y-3 p-4 rounded-lg border border-[hsl(185,72%,42%)]/20 bg-[hsl(185,72%,42%)]/5">
            <p className="text-xs font-semibold text-[hsl(215,25%,17%)]">Your Contact Details</p>
            <input
              type="text"
              placeholder="Full Name *"
              value={contactName}
              onChange={e => setContactName(e.target.value)}
              className="w-full px-3 py-2 rounded-md border border-[hsl(220,15%,85%)] text-sm text-[hsl(215,25%,17%)] placeholder:text-[hsl(215,12%,50%)]/50 focus:outline-none focus:ring-2 focus:ring-[hsl(185,72%,42%)]/30"
            />
            <input
              type="email"
              placeholder="Email Address *"
              value={contactEmail}
              onChange={e => setContactEmail(e.target.value)}
              className="w-full px-3 py-2 rounded-md border border-[hsl(220,15%,85%)] text-sm text-[hsl(215,25%,17%)] placeholder:text-[hsl(215,12%,50%)]/50 focus:outline-none focus:ring-2 focus:ring-[hsl(185,72%,42%)]/30"
            />
            <input
              type="tel"
              placeholder="Phone Number (optional)"
              value={contactPhone}
              onChange={e => setContactPhone(e.target.value)}
              className="w-full px-3 py-2 rounded-md border border-[hsl(220,15%,85%)] text-sm text-[hsl(215,25%,17%)] placeholder:text-[hsl(215,12%,50%)]/50 focus:outline-none focus:ring-2 focus:ring-[hsl(185,72%,42%)]/30"
            />
            <div className="flex gap-2">
              <Button
                size="sm"
                className="flex-1 bg-[hsl(185,72%,42%)] hover:bg-[hsl(185,72%,36%)] text-white font-bold tracking-wide"
                onClick={handleSubmitEnquiry}
              >
                Submit Enquiry
              </Button>
              <Button variant="outline" size="sm" onClick={() => setShowContactForm(false)}>Cancel</Button>
            </div>
          </div>
        )}

        {!showContactForm && (
          <Button
            size="sm"
            className={`w-full font-bold tracking-wide ${
              enquirySent
                ? "bg-[hsl(185,72%,42%)]/15 text-[hsl(185,72%,36%)] border border-[hsl(185,72%,42%)]/30 cursor-default"
                : "bg-[hsl(185,72%,42%)] hover:bg-[hsl(185,72%,36%)] text-white"
            }`}
            disabled={enquirySent}
            onClick={() => setShowContactForm(true)}
          >
            {enquirySent ? "Enquiry Submitted ✓" : "Request More Information"}
          </Button>
        )}
      </div>
    </div>
  );
};

const Fleet = () => {
  const { companyName } = usePortalCompany();
  const clientKey = resolveClientKey(companyName);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [selectedVessel, setSelectedVessel] = useState<any>(null);

  const vessels = useMemo(() => {
    const clientVessels = clientKey ? getClientVessels(clientKey) : MOCK_VESSELS;
    return clientVessels.map((v, i) => ({
      ...v,
      image: (v.client === "Client B" || v.client === "Client D" || v.client === "Client C") ? aircatSonangol : vesselImages[i % vesselImages.length],
    }));
  }, [clientKey]);

  const onHireCount = vessels.filter(v => ["on_charter", "available", "in_transit", "alongside"].includes(v.status)).length;

  return (
    <PortalLayout>
      <div className="flex items-start justify-between mb-6">
        <div>
          <p className="text-[11px] font-bold tracking-[3px] uppercase text-[hsl(211,100%,40%)]/60 mb-1">Operations</p>
          <h1 className="text-3xl font-bold text-[hsl(215,25%,17%)]">Fleet & Vessels</h1>
          <p className="text-sm text-[hsl(215,12%,50%)] mt-1">{vessels.length} contracted vessel{vessels.length !== 1 ? "s" : ""}</p>
        </div>
      </div>

      <Tabs defaultValue="fleet" className="space-y-4">
        <TabsList className="bg-white border border-[hsl(220,15%,90%)]">
          <TabsTrigger value="fleet">Your Fleet</TabsTrigger>
          <TabsTrigger value="catalogue">Velocity Offshore Vessel Catalogue</TabsTrigger>
        </TabsList>

        <TabsContent value="fleet">
          {/* View toggle */}
          <div className="flex justify-end mb-4">
            <div className="flex items-center gap-1 bg-[hsl(220,20%,97%)] rounded-lg p-1">
              <button onClick={() => setViewMode("grid")} className={`p-2 rounded-md transition-all ${viewMode === "grid" ? "bg-[hsl(211,100%,40%)]/10 text-[hsl(211,100%,40%)]" : "text-[hsl(215,12%,50%)]"}`}>
                <LayoutGrid className="w-4 h-4" />
              </button>
              <button onClick={() => setViewMode("list")} className={`p-2 rounded-md transition-all ${viewMode === "list" ? "bg-[hsl(211,100%,40%)]/10 text-[hsl(211,100%,40%)]" : "text-[hsl(215,12%,50%)]"}`}>
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* KPI Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            {[
              { label: "Vessels On-Hire", value: `${onHireCount}/${vessels.length}`, icon: Ship, color: "#28A745" },
              { label: "Fleet Utilization", value: "94.2%", icon: Gauge, color: "#0066CC" },
              { label: "On-Time Performance", value: "98.1%", icon: Anchor, color: "#0066CC" },
              { label: "Total PAX Capacity", value: vessels.reduce((s, v) => s + v.paxCapacity, 0).toString(), icon: Users, color: "#17A2B8" },
            ].map((kpi) => {
              const Icon = kpi.icon;
              return (
                <div key={kpi.label} className="portal-card p-4">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: kpi.color + "18" }}>
                    <Icon className="w-4 h-4" style={{ color: kpi.color }} />
                  </div>
                  <p className="text-2xl font-bold text-[hsl(215,25%,17%)]">{kpi.value}</p>
                  <p className="text-[11px] text-[hsl(215,12%,50%)] mt-1">{kpi.label}</p>
                </div>
              );
            })}
          </div>

          {vessels.length === 0 ? (
            <div className="portal-card p-16 text-center">
              <Ship className="w-12 h-12 text-[hsl(215,12%,50%)]/30 mx-auto mb-4" />
              <p className="text-[hsl(215,12%,50%)]">Your contracted vessels will appear here.</p>
            </div>
          ) : viewMode === "grid" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {vessels.map((vessel, idx) => {
                const st = statusConfig[vessel.status] || statusConfig.available;
                return (
                  <div key={vessel.id} className="portal-card overflow-hidden cursor-pointer hover:shadow-md transition-all" onClick={() => setSelectedVessel(vessel)}>
                    <div className="relative h-44 overflow-hidden">
                      <img src={vessel.image} alt={vessel.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                      <div className="absolute top-3 right-3">
                        <span className={`px-2.5 py-1 rounded text-[10px] font-bold tracking-[1px] uppercase ${st.color}`}>{st.label}</span>
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[hsl(215,25%,17%)]/90 to-transparent pt-12 pb-3 px-4">
                        <h3 className="font-bold text-lg text-white">{vessel.name}</h3>
                        <p className="text-[11px] text-white/60">{vessel.type}</p>
                      </div>
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 text-xs text-[hsl(215,12%,50%)] mb-3">
                        <MapPin className="w-3 h-3 text-[hsl(211,100%,40%)]/60" />
                        <span className="truncate">{vessel.destination || vessel.homePort || vessel.region}</span>
                      </div>
                      <div className="grid grid-cols-4 gap-2 text-center">
                        {[
                          { label: "Region", value: vessel.region },
                          { label: "PAX Cap", value: vessel.paxCapacity.toString() },
                          { label: "Max Speed", value: `${vessel.maxSpeed} kts` },
                          { label: "Captain", value: vessel.captain.replace("Capt. ", "") },
                        ].map(stat => (
                          <div key={stat.label}>
                            <p className="text-xs font-medium text-[hsl(215,25%,17%)]">{stat.value}</p>
                            <p className="text-[9px] text-[hsl(215,12%,50%)] mt-0.5">{stat.label}</p>
                          </div>
                        ))}
                      </div>
                      <div className="mt-3 pt-3 border-t border-[hsl(220,15%,90%)] flex items-center justify-between">
                        <span className="text-[10px] text-[hsl(211,100%,40%)]">Click for vessel status</span>
                        <ChevronRight className="w-3.5 h-3.5 text-[hsl(211,100%,40%)]" />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="space-y-2">
              {vessels.map((vessel) => {
                const st = statusConfig[vessel.status] || statusConfig.available;
                return (
                  <div key={vessel.id} className="portal-card p-4 flex items-center gap-4 cursor-pointer hover:shadow-md transition-all" onClick={() => setSelectedVessel(vessel)}>
                    <img src={vessel.image} alt={vessel.name} className="w-20 h-14 rounded-lg object-cover flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-bold text-sm text-[hsl(215,25%,17%)]">{vessel.name}</h3>
                        <span className={`px-2 py-0.5 rounded text-[9px] font-bold tracking-[1px] uppercase ${st.color}`}>{st.label}</span>
                      </div>
                      <p className="text-[11px] text-[hsl(215,12%,50%)]">{vessel.type} · {vessel.region}</p>
                    </div>
                    <div className="hidden md:flex items-center gap-6 text-center">
                      {[
                        { label: "PAX", value: vessel.paxCapacity.toString() },
                        { label: "Speed", value: `${vessel.maxSpeed} kts` },
                        { label: "Captain", value: vessel.captain.replace("Capt. ", "") },
                      ].map(stat => (
                        <div key={stat.label}>
                          <p className="text-xs font-medium text-[hsl(215,25%,17%)]">{stat.value}</p>
                          <p className="text-[9px] text-[hsl(215,12%,50%)]">{stat.label}</p>
                        </div>
                      ))}
                    </div>
                    <ChevronRight className="w-4 h-4 text-[hsl(211,100%,40%)]" />
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="catalogue">
          <div className="mb-4 flex items-center justify-between gap-4">
            <p className="text-sm text-[hsl(215,12%,50%)]">
              Explore additional Velocity Offshore vessel classes available for charter or newbuild projects.
            </p>
            <a
              href="/fleet-brochure"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-md border border-teal/40 px-3 py-2 text-xs font-semibold text-teal hover:bg-teal/10 whitespace-nowrap"
            >
              Meet Our Fleet Brochure
            </a>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {VESSEL_CATALOGUE.map(v => (
              <CatalogueVesselCard key={v.id} vessel={v} />
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {selectedVessel && <VesselStatusModal vessel={selectedVessel} onClose={() => setSelectedVessel(null)} />}
    </PortalLayout>
  );
};

export default Fleet;
